import {
  customerFollowUpQueueSchema,
  taskSchema,
  type CustomerFollowUpQueue,
  type FollowUpQueueScope,
  type SetTaskStatusInput,
  type Task,
  type TaskStatus,
} from '@tailoring-crm/shared';
import type { ApiClient } from '../api/client.js';

/**
 * The follow-ups queue: every open task on the shop's customers, and the one write the queue's
 * rows make — ticking a task off.
 *
 * Every response is validated against the SAME schema the server validated its output with, so a
 * field that quietly changes shape on one side fails loudly rather than rendering as `undefined`.
 */

/**
 * `dueBefore` is an instant, not a count: the header panel asks for "everything owed up to the
 * end of tomorrow" and the server already knows how to answer that. Omitting it means the whole
 * open queue, which is what the full page wants.
 */
export async function getFollowUpQueue(
  client: ApiClient,
  scope: FollowUpQueueScope = 'mine',
  dueBefore?: string,
): Promise<CustomerFollowUpQueue> {
  const query = new URLSearchParams({ scope });
  if (dueBefore) query.set('dueBefore', dueBefore);
  return await client.get(`/customers/follow-ups?${query.toString()}`, {
    schema: customerFollowUpQueueSchema,
  });
}

/** Opening, completing or cancelling one of a customer's tasks. */
export async function setCustomerTaskStatus(
  client: ApiClient,
  customerId: string,
  taskId: string,
  status: TaskStatus,
): Promise<Task> {
  const input: SetTaskStatusInput = { status };
  return await client.post(`/customers/${customerId}/tasks/${taskId}/status`, input, {
    schema: taskSchema,
  });
}
